/**
 * Vue Router Configuration
 * Basic routing for cohort builder SPA with OAuth callback support and authentication guards
 */
import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useUIStore } from '@/stores/ui'
import { useI18n } from '@/composables/useI18n'
import { getAuthConfig } from '@/config/auth.config'
import { pluginConfigService } from '@/services/PluginConfigService'
import { logger } from '@/utils/logger'
import { foldSearchIntoHash } from './legacy-url'
import { routes } from './routes'

// Fold any real (pre-hash) query string into the hash before the hash history
// captures the current location, so deeplink/OAuth guards see ?cohortId / ?token.
if (typeof window !== 'undefined') {
  foldSearchIntoHash()
}

const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes,
})

// Deeplink guard - handles ?cohortId=X and ?route=/path on initial load
let deeplinkProcessed = false

router.beforeEach(
  (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    if (deeplinkProcessed) {
      next()
      return
    }

    // Skip on auth callbacks
    if (to.meta.isOAuthCallback || to.meta.isSAMLCallback || to.meta.isOpenIDCallback) {
      deeplinkProcessed = true
      next()
      return
    }

    const cohortId = to.query.cohortId as string
    const routeParam = to.query.route as string

    if (cohortId) {
      deeplinkProcessed = true
      logger.info('Router', `Deeplink: redirecting to cohort ${cohortId}`)
      next({ path: `/cohorts/${cohortId}`, replace: true })
      return
    }

    if (routeParam) {
      deeplinkProcessed = true
      const targetRoute = routeParam.startsWith('/') ? routeParam : `/${routeParam}`

      // Validate route exists to prevent open redirect
      const resolved = router.resolve(targetRoute)
      if (resolved.matched.length === 0 || resolved.name === 'not-found') {
        logger.warn('Router', `Deeplink: invalid route ${targetRoute}, ignoring`)
        next()
        return
      }

      logger.info('Router', `Deeplink: redirecting to route ${targetRoute}`)
      next({ path: targetRoute, replace: true })
      return
    }
    deeplinkProcessed = true
    next()
  }
)


router.afterEach(to => {
  if (to.meta.isOAuthCallback || to.meta.isSAMLCallback || to.meta.isOpenIDCallback) {
    return
  }

  const authStore = useAuthStore()
  void authStore.refreshUser().catch(error => {
    logger.warn('Router', 'Permission refresh after navigation failed', error)
  })
})
// Home redirect guard - must run before auth guard
router.beforeEach(
  (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    if (to.path === '/') {
      const logoNavigateTo = pluginConfigService.getLogoNavigateTo()
      if (logoNavigateTo && logoNavigateTo !== '/') {
        next(logoNavigateTo)
        return
      }
    }
    next()
  }
)

// OAuth/SAML/OpenID callback handler
router.beforeEach(async (to, _from, next) => {
  const isAuthCallback =
    to.meta.isOAuthCallback ||
    to.meta.isSAMLCallback ||
    to.meta.isOpenIDCallback ||
    to.path === '/oauth/callback' ||
    to.path === '/saml/callback' ||
    to.path === '/openid/callback'

  if (isAuthCallback) {
    const authStore = useAuthStore()

    try {
      // OIDC one-time-code handoff: the provider redirects here with
      // ?code={otc} instead of setting a token in storage/cookies. Redeem it
      // via WebAPI before falling back to the other handoff mechanisms.
      const otcCode = to.query.code as string | undefined
      if (otcCode) {
        const { authService } = await import('@/services/auth/authService')
        const jwt = await authService.redeemOTC(otcCode)
        authStore.setToken(jwt)
        await authStore.refreshUser()
        authStore.setAuthClient('OIDC')

        const oidcDestination = sessionStorage.getItem('oauth_redirect_destination')
        sessionStorage.removeItem('oauth_redirect_destination')

        next(oidcDestination || '/')
        return
      }

      // Check for token in localStorage (Atlas pattern - backend sets this)
      const localStorageToken = localStorage.getItem('bearerToken')
      if (localStorageToken && localStorageToken !== 'null' && localStorageToken !== 'undefined') {
        authStore.setToken(localStorageToken)

        await authStore.refreshUser()
        authStore.setAuthClient('OpenID')

        // Restore destination URL or redirect to home
        const destination = sessionStorage.getItem('oauth_redirect_destination')
        sessionStorage.removeItem('oauth_redirect_destination')

        next(destination || '/')
        return
      }

      // Check for token in cookies (WebAPI might set it there)
      const cookieToken = getCookieToken()
      if (cookieToken) {
        logger.info('Router', 'Token found in cookie')
        authStore.setToken(cookieToken)

        await authStore.refreshUser()

        // Clear the cookie token
        document.cookie = 'bearerToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'

        // Restore destination URL or redirect to home
        const destination = sessionStorage.getItem('oauth_redirect_destination')
        sessionStorage.removeItem('oauth_redirect_destination')

        next(destination || '/')
        return
      }

      // No token found - OAuth failed
      logger.error('Router', 'No token received from OAuth provider')
      authStore.setError(useI18n().tv('auth.oauthNoToken'))
      next('/')
      return
    } catch (error) {
      logger.error('Router', 'OAuth callback error', error)
      authStore.setError(error instanceof Error ? error.message : useI18n().tv('auth.authenticationFailed'))
      next('/')
      return
    }
  }

  next()
})

/**
 * Authentication guard - check if route requires authentication
 */
router.beforeEach(
  (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
    // Skip auth check for OAuth/SAML/OpenID callbacks (handled by their specific guard)
    if (to.meta.isOAuthCallback || to.meta.isSAMLCallback || to.meta.isOpenIDCallback) {
      next()
      return
    }

    // Skip auth check if authentication is disabled
    if (!getAuthConfig().userAuthenticationEnabled) {
      next()
      return
    }

    // Check if route requires authentication
    const requiresAuth = to.meta.requiresAuth === true // Default to false unless explicitly set to true

    if (requiresAuth) {
      const authStore = useAuthStore()

      // Open the login modal only once we've resolved the subject and found
      // neither an authenticated nor an anonymous one. Deferring on
      // `userResolved` avoids prompting during the initial user/me round-trip;
      // `!authStore.user` keeps an anonymous subject (with server-granted
      // permissions) from ever triggering the prompt.
      if (
        !authStore.isAuthenticated &&
        authStore.userResolved &&
        !authStore.user &&
        getAuthConfig().userAuthenticationEnabled
      ) {
        logger.debug('Router', 'Route requires auth, opening login modal')
        authStore.openLoginModal()

        // Allow navigation anyway - login modal will overlay
        // This prevents redirect loops and allows the app to render
        next()
        return
      }
    }

    next()
  }
)

/**
 * Configuration Panel Guard - Auto-close panel on navigation
 */
router.beforeEach(
  (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    // Auto-close config panel when navigating to a different route
    if (from.path !== to.path) {
      const uiStore = useUIStore()

      if (uiStore.configPanelState.isOpen) {
        uiStore.closeConfigPanel()
      }
    }

    next()
  }
)

const APP_TITLE = 'ATLAS'

function applyDocumentTitle(route: RouteLocationNormalized): void {
  if (typeof document === 'undefined') return
  const titleKey = (route.meta as { titleKey?: string }).titleKey
  if (!titleKey) {
    document.title = APP_TITLE
    return
  }
  let label: string = titleKey
  try {
    label = useI18n().tv(titleKey)
  } catch {
    // Pinia not yet installed (e.g. early in bootstrap or in isolated tests);
    // fall back to the title key itself.
  }
  document.title = `${label} | ${APP_TITLE}`
}

router.afterEach((to: RouteLocationNormalized) => {
  applyDocumentTitle(to)
})

// This module is evaluated before Pinia is installed (main.ts imports the router
// at load time), so the locale store cannot be watched from here; the store
// dispatches `locale-changed` for exactly this case.
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('locale-changed', () => {
    applyDocumentTitle(router.currentRoute.value)
  })
}

/**
 * Get token from cookie
 */
function getCookieToken(): string | null {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === 'bearerToken' && value) {
      return decodeURIComponent(value)
    }
  }
  return null
}

export default router
